import classNames from 'classnames';
import React, { useState } from 'react';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';
import { SingleValue } from 'react-select';

import { FiCalendar } from '@vyaguta/vyaguta-icons/fi';
import { UiArrowLeft, UiArrowRight } from '@vyaguta/vyaguta-icons/ui';

import 'react-dates/lib/css/_datepicker.css';

import { MONTH_MAPPING } from 'constants/date';

import { getCalendarMonthsOption, getCalendarYearsOption, getWeek } from 'utils/date';

import DatePickerDropdown from './DatePickerDropdown';

type Props = {
  customClassName?: string;
  hasError?: boolean;
  placeholderText?: string;
} & Omit<
  React.ComponentProps<typeof SingleDatePicker>,
  'focused' | 'onFocusChange' | 'renderMonthElement' | 'id' | 'placeholder'
>;

type DatePickerDropdownOption = {
  label: string | number;
  value: number;
};

type MonthElementProps = {
  month: moment.Moment;
  onMonthSelect: (currentMonth: moment.Moment, monthNumber: number) => void;
  onYearSelect: (currentMonth: moment.Moment, yearNumber: number) => void;
};

const DatePicker: React.FC<Props> = ({ customClassName, hasError, placeholderText, onDateChange, ...rest }) => {
  const [focused, setFocused] = useState(false);

  const renderMonthElement = ({ month, onMonthSelect, onYearSelect }: MonthElementProps) => {
    const handleMonthSelect = (
      currentMonth: moment.Moment,
      monthSelectHandler: (monthValue: moment.Moment, monthNumber: number) => void,
      event: SingleValue<DatePickerDropdownOption>
    ) => {
      if (!event) return;
      return monthSelectHandler(currentMonth, event.value);
    };

    const handleYearSelect = (
      currentMonth: moment.Moment,
      yearSelectHandler: (monthValue: moment.Moment, yearNumber: number) => void,
      event: SingleValue<DatePickerDropdownOption>
    ) => {
      if (!event) return;
      return yearSelectHandler(currentMonth, event.value);
    };

    const monthLabelMap = MONTH_MAPPING as Record<number, string>;
    const monthValue = { label: monthLabelMap[month.month()], value: month.month() };

    const yearValue = { label: month.year(), value: month.year() };

    const onMonthChange = (event: SingleValue<DatePickerDropdownOption>) =>
      handleMonthSelect(month, onMonthSelect, event);

    const onYearChange = (event: SingleValue<DatePickerDropdownOption>) => handleYearSelect(month, onYearSelect, event);

    return (
      <div>
        <div className="d-flex justify-content-center align-items-start gap-px-8">
          {DatePickerDropdown(getCalendarMonthsOption(), monthValue, onMonthChange)}

          {DatePickerDropdown(getCalendarYearsOption(), yearValue, onYearChange)}
        </div>

        <div className="d-flex date-picker-week-header">
          {getWeek().map((item) => (
            <p key={item} className="title">
              {item}
            </p>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={classNames({ error: hasError, focused }, customClassName)}>
      <SingleDatePicker
        id="date"
        placeholder={placeholderText || 'Select Date'}
        focused={focused}
        onDateChange={onDateChange}
        displayFormat="LL"
        numberOfMonths={1}
        customInputIcon={<FiCalendar size={20} className="color-grey-50" />}
        navPrev={<UiArrowLeft size={20} className="color-grey-80" />}
        navNext={<UiArrowRight size={20} className="color-grey-80" />}
        onFocusChange={({ focused: nextFocused }) => setFocused(Boolean(nextFocused))}
        inputIconPosition={ICON_AFTER_POSITION}
        hideKeyboardShortcutsPanel={true}
        isOutsideRange={() => false}
        renderMonthElement={renderMonthElement}
        {...rest}
      />
    </div>
  );
};

export default DatePicker;
